import { ApiProblemError } from "./authManager";
import { api } from "./apiClient";
import { isLocalMode } from "./appMode";
import { loseAccess } from "./localAccess";
import { AccessRevokedError, getServerToken } from "./localSession";
import type { ConnectionCandidate, ProblemDetails, ResourceItem } from "@rago/web-shared";

/**
 * Per-server connection manager (ARCHITECTURE §4a client contract): race the
 * candidate list (local → relay, HTTPS-first), probe /v1/server and assert
 * the serverId (DNS-rebind / misroute defense), pin the winner, and manage
 * the server-scoped token (re-mint via the cloud broker on 401 or expiry).
 *
 * The manager is also an observable store (the ServerWatchState pattern): the
 * UI subscribes to a ConnectionSnapshot to render Local/Remote status live.
 * Every emission is deferred a microtask because mutations can be reached
 * synchronously from a React render (imageUrl warms tokens, applyResources
 * refreshes candidates) — a sync notify would setState mid-render.
 */

const PROBE_LOCAL_MS = 3000;
const PROBE_RELAY_MS = 5000;
const PIN_KEY = (serverId: string) => `rago.conn.${serverId}`;

interface Pinned {
  uri: string;
  testedAt: number;
}

/** How this device reaches the server, from the candidate's own flags. */
export type ConnectionKind = "local" | "remote" | "relay";

export type ConnectionPhase = "disconnected" | "connecting" | "connected" | "failed";

export interface ConnectionSnapshot {
  phase: ConnectionPhase;
  /** Established base URI (null before the first successful establish). */
  uri: string | null;
  /** Winning candidate, URI-matched from candidates; null = unknown route. */
  active: ConnectionCandidate | null;
  /** Derived from active.local/.relay — never from hostname shapes. */
  kind: ConnectionKind | null;
  /** Last pinned URI from localStorage (survives reload; unverified). */
  pinnedUri: string | null;
  candidates: ConnectionCandidate[];
  error: string | null;
  /** Sticky: this device has verified a path to the server (an identity-
   * checked probe or a resolved response) and no race has failed since.
   * Unlike `phase`, a re-race in flight does not clear it — the registry's
   * effective-online derivation reads this, so a Retry never blinks the
   * page to the unavailable state. */
  reachable: boolean;
  /** Sticky the other way: the last race found no candidate answering, and
   * nothing has proven a path since. A re-race in flight does not clear it
   * either, so a server this device cannot reach stays "unavailable" while
   * it is tried again — whatever the broker says about its check-ins. */
  unreachable: boolean;
}

export interface CandidateProbe {
  uri: string;
  ok: boolean;
  latencyMs: number | null;
}

export function classifyCandidate(c: ConnectionCandidate): ConnectionKind {
  if (c.relay) return "relay";
  return c.local ? "local" : "remote";
}

/** The owner's own URL (reverse proxy, VPN, own domain): a remote candidate
 * the broker did not synthesize from an address, so it carries no
 * directHostname. */
export function isCustomCandidate(c: ConnectionCandidate): boolean {
  return !c.local && !c.relay && !c.directHostname;
}

/** Connection priority, lowest first: the owner's custom URL beats every
 * synthesized path, the LAN beats the public address, the relay is last. */
export function candidateRank(c: ConnectionCandidate): 0 | 1 | 2 | 3 {
  if (isCustomCandidate(c)) return 0;
  if (c.local) return 1;
  return c.relay ? 3 : 2;
}

export function connectionKindLabel(kind: ConnectionKind): string {
  return kind === "local" ? "Local" : kind === "relay" ? "Remote (relay)" : "Remote";
}

/** Stable identity for the no-source case (useSyncExternalStore). */
export const EMPTY_CONNECTION_SNAPSHOT: ConnectionSnapshot = {
  phase: "disconnected",
  uri: null,
  active: null,
  kind: null,
  pinnedUri: null,
  candidates: [],
  error: null,
  reachable: false,
  unreachable: false,
};

function sameCandidates(a: ConnectionCandidate[], b: ConnectionCandidate[]): boolean {
  return (
    a.length === b.length &&
    a.every((c, i) => {
      const o = b[i];
      return o !== undefined && c.uri === o.uri && c.local === o.local && c.relay === o.relay;
    })
  );
}

export class ServerConnection {
  readonly serverId: string;
  private candidates: ConnectionCandidate[];
  private token: string;
  private tokenExpiresAt: number;
  private baseUrl: string | null = null;
  private establishing: Promise<string> | null = null;
  private minting: Promise<string> | null = null;

  private phase: ConnectionPhase = "disconnected";
  private reachable = false;
  private unreachable = false;
  private lastError: string | null = null;
  private pinnedUri: string | null;
  private listeners = new Set<() => void>();
  private snap: ConnectionSnapshot;
  private emitQueued = false;

  constructor(resource: ResourceItem) {
    this.serverId = resource.serverId;
    this.candidates = resource.connections ?? [];
    this.token = resource.serverToken?.token ?? "";
    this.tokenExpiresAt = resource.serverToken
      ? Date.parse(resource.serverToken.expiresAt)
      : 0;
    this.pinnedUri = this.readPin()?.uri ?? null;
    this.snap = this.buildSnap();
  }

  /** Refresh candidates and token from a newer resources payload. */
  update(resource: ResourceItem): void {
    // Reassign (and re-derive the snapshot) only on a real change so the 60 s
    // resources refetch is silent and snapshot identity stays stable.
    if (resource.connections && !sameCandidates(this.candidates, resource.connections)) {
      this.candidates = resource.connections;
      this.scheduleEmit();
      // A changed list while not connected (a fresh claim growing its first
      // endpoints, a server that moved networks) re-races on its own — the
      // failed phase must not latch until a manual "Test connections". So
      // does a connected path the new list no longer justifies (see
      // pathStale). Deferred a microtask: update() can be reached from a
      // React render.
      if ((this.phase !== "connected" || this.pathStale()) && this.candidates.length > 0) {
        queueMicrotask(() => {
          if (this.phase === "connected" && !this.pathStale()) return;
          void this.establish(true).catch(() => {});
        });
      }
    }
    if (resource.serverToken) {
      this.token = resource.serverToken.token;
      this.tokenExpiresAt = Date.parse(resource.serverToken.expiresAt);
    }
  }

  /** Connected over a path the current candidate list no longer justifies:
   * the pinned URI vanished (the server moved to another port), or a
   * candidate of a better rank appeared (the owner added a custom URL, a
   * direct path came back while we sit on the relay) — a fallback must not
   * outlive the reason for it. */
  private pathStale(): boolean {
    if (this.phase !== "connected" || !this.baseUrl) return false;
    const active = this.candidates.find((c) => c.uri === this.baseUrl);
    if (!active) return true;
    const rank = candidateRank(active);
    return this.candidates.some((c) => candidateRank(c) < rank);
  }

  // ---- observable surface ----

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  /** Cached snapshot for useSyncExternalStore (identity changes on emit only). */
  snapshot = (): ConnectionSnapshot => this.snap;

  /** Re-render tick for asset-token consumers (<img> src embeds the token). */
  notifyAssets(): void {
    this.scheduleEmit();
  }

  private buildSnap(): ConnectionSnapshot {
    const active = this.baseUrl
      ? (this.candidates.find((c) => c.uri === this.baseUrl) ?? null)
      : null;
    return {
      phase: this.phase,
      uri: this.baseUrl,
      active,
      kind: active ? classifyCandidate(active) : null,
      pinnedUri: this.pinnedUri,
      candidates: this.candidates,
      error: this.lastError,
      reachable: this.reachable,
      unreachable: this.unreachable,
    };
  }

  /** Deferred rebuild+notify; back-to-back changes coalesce into one emit. */
  private scheduleEmit(): void {
    if (this.emitQueued) return;
    this.emitQueued = true;
    queueMicrotask(() => {
      this.emitQueued = false;
      this.snap = this.buildSnap();
      for (const fn of this.listeners) fn();
    });
  }

  /** Any resolved HTTP response proves the pinned path works. */
  private markReachable(): void {
    if (this.phase === "connected" && this.reachable) return;
    this.phase = "connected";
    this.reachable = true;
    this.unreachable = false;
    this.lastError = null;
    this.scheduleEmit();
  }

  /** Sync read of the sticky reachability bit (registry derivation). */
  isReachable(): boolean {
    return this.reachable;
  }

  /** Sync read of the sticky "tried, nothing answered" bit (registry
   * derivation): this device's own verdict, which outranks a recent
   * check-in at the broker. */
  isUnreachable(): boolean {
    return this.unreachable;
  }

  /** Sync candidate count — the snapshot lags update() by a microtask. */
  candidateCount(): number {
    return this.candidates.length;
  }

  private orderedCandidates(): ConnectionCandidate[] {
    // Priority order (custom → local → public → relay); the race takes the
    // first candidate that answers in this order. Within a rank the pinned
    // URI goes first so the common path is settled by one probe — a pin
    // never lifts a candidate above a better-ranked one.
    const pinnedUri = this.readPin()?.uri ?? null;
    return [...this.candidates].sort((a, b) => {
      const byRank = candidateRank(a) - candidateRank(b);
      if (byRank !== 0) return byRank;
      return Number(b.uri === pinnedUri) - Number(a.uri === pinnedUri);
    });
  }

  private readPin(): Pinned | null {
    try {
      const raw = localStorage.getItem(PIN_KEY(this.serverId));
      return raw ? (JSON.parse(raw) as Pinned) : null;
    } catch {
      return null;
    }
  }

  private writePin(uri: string): void {
    try {
      localStorage.setItem(PIN_KEY(this.serverId), JSON.stringify({ uri, testedAt: Date.now() }));
    } catch {
      // best effort
    }
    this.baseUrl = uri;
    this.pinnedUri = uri;
    this.phase = "connected";
    this.reachable = true;
    this.unreachable = false;
    this.lastError = null;
    this.scheduleEmit();
  }

  private async probe(candidate: ConnectionCandidate, timeoutMs: number): Promise<string | null> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(`${candidate.uri}/v1/server`, { signal: controller.signal });
      if (!res.ok) return null;
      const identity = (await res.json()) as { serverId?: string };
      // Reject a misrouted/rebound host answering for a different server.
      if (identity.serverId !== this.serverId) return null;
      return candidate.uri;
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  /** Establish (or re-establish) the base URL by racing candidates. */
  async establish(force = false): Promise<string> {
    if (this.baseUrl && !force) return this.baseUrl;
    if (this.establishing) return this.establishing;
    this.establishing = (async () => {
      this.phase = "connecting";
      this.lastError = null;
      this.scheduleEmit();
      const candidates = this.orderedCandidates();
      const locals = candidates.filter((c) => c.local || !c.relay);
      const relays = candidates.filter((c) => c.relay);

      const winner =
        (await this.race(locals, PROBE_LOCAL_MS)) ?? (await this.race(relays, PROBE_RELAY_MS));
      if (!winner) {
        this.phase = "failed";
        this.reachable = false;
        this.unreachable = true;
        this.lastError = "No reachable connection candidate";
        this.scheduleEmit();
        throw new Error("no reachable connection candidate");
      }
      this.writePin(winner);
      return winner;
    })();
    try {
      return await this.establishing;
    } finally {
      this.establishing = null;
    }
  }

  /** Background re-verification for a server nothing else dials (the broker
   * reports it offline, or this device could not reach it):
   * a healthy pinned path is probed in place (no re-pin, so a playing
   * session never has its base URL swapped under it); only a failed probe
   * — or no path yet — re-races the list. Never throws. */
  async recheck(): Promise<boolean> {
    if (this.establishing) {
      try {
        await this.establishing;
      } catch {
        // the failed race already cleared `reachable`
      }
      return this.reachable;
    }
    const active = this.baseUrl
      ? (this.candidates.find((c) => c.uri === this.baseUrl) ?? null)
      : null;
    if (this.reachable && active) {
      const res = await this.probeCandidate(active);
      if (res.ok) return true;
    }
    try {
      await this.establish(true);
      return true;
    } catch {
      return false;
    }
  }

  private async race(candidates: ConnectionCandidate[], timeoutMs: number): Promise<string | null> {
    if (candidates.length === 0) return null;
    const probes = candidates.map((c) => this.probe(c, timeoutMs));
    const results = await Promise.all(probes);
    return results.find((uri): uri is string => uri !== null) ?? null;
  }

  /** A scoped token with more than a minute left, else one mint shared by
   * every caller waiting on it — ten queries waking together after a sleep
   * must not mint ten tokens. */
  private async freshToken(): Promise<string> {
    if (this.token && Date.now() < this.tokenExpiresAt - 60_000) return this.token;
    this.minting ??= this.mintToken().finally(() => {
      this.minting = null;
    });
    return this.minting;
  }

  private async mintToken(): Promise<string> {
    if (isLocalMode) {
      // The server-hosted app: the token ladder — a broker mint while the
      // cloud answers, else the server's own local session (offline). An
      // emptied token means the server answered 401 with what we had, so
      // only a different token helps: skip the stored rungs.
      const t = await getServerToken(this.serverId, { force: this.token === "" });
      this.token = t.token;
      this.tokenExpiresAt = t.expiresAt;
      return this.token;
    }
    const res = await api<{ token: string; expiresAt: string }>(
      `/v1/servers/${this.serverId}/token`,
      { method: "POST" },
    );
    this.token = res.token;
    this.tokenExpiresAt = Date.parse(res.expiresAt);
    return this.token;
  }

  /** Absolute URL against the pinned/established base, for <img>/<video> src. */
  async url(path: string): Promise<string> {
    const base = await this.establish();
    return `${base}${path}`;
  }

  /** Probe one candidate without touching the pin (Network settings chips). */
  async probeCandidate(candidate: ConnectionCandidate): Promise<CandidateProbe> {
    const started = performance.now();
    const uri = await this.probe(candidate, candidate.relay ? PROBE_RELAY_MS : PROBE_LOCAL_MS);
    return {
      uri: candidate.uri,
      ok: uri !== null,
      latencyMs: uri !== null ? Math.round(performance.now() - started) : null,
    };
  }

  /** Current base URL if established (sync; empty before first establish). */
  baseUrlSync(): string {
    return this.baseUrl ?? "";
  }

  currentToken(): string {
    return this.token;
  }

  /** Authenticated JSON request with the connection/token retry ladder.
   * `signal` is the caller's own limit on the wait (a best-effort nudge that
   * must not linger): once it fires the request ends there, without the
   * ladder's second try. */
  async request<T>(
    path: string,
    init?: { method?: string; body?: unknown; signal?: AbortSignal },
  ): Promise<T> {
    const send = async (): Promise<Response> => {
      const base = await this.establish();
      const token = await this.freshToken();
      const headers: Record<string, string> = { authorization: `Bearer ${token}` };
      if (init?.body !== undefined) headers["content-type"] = "application/json";
      return fetch(`${base}${path}`, {
        method: init?.method ?? (init?.body !== undefined ? "POST" : "GET"),
        headers,
        ...(init?.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
        ...(init?.signal ? { signal: init.signal } : {}),
      });
    };

    let res: Response;
    try {
      res = await send();
    } catch (err) {
      // A verdict on the account's access, not a dead path: there is
      // nothing to re-race. The broker's own is the end of the session here.
      if (err instanceof AccessRevokedError) {
        if (err.definitive) void loseAccess();
        throw err;
      }
      // The caller stopped waiting: that says nothing about the path.
      if (init?.signal?.aborted) throw err;
      // Network error: the pinned path may be dead — re-establish once.
      await this.establish(true);
      res = await send();
    }
    this.markReachable();
    if (res.status === 401) {
      // Scoped token expired or rejected: force a re-mint and retry once.
      this.token = "";
      res = await send();
    }
    if (res.status === 204) return undefined as T;
    const json = await res.json().catch(() => null);
    if (!res.ok) {
      // rago-server answers with RFC-7807 problems; surface them as
      // ApiProblemError so <ErrorNote> renders the server's own words
      // ("That folder already belongs to another library") instead of a
      // stringified blob.
      const problem = json as Partial<ProblemDetails> | null;
      if (problem && typeof problem.title === "string") {
        throw new ApiProblemError(problem as ProblemDetails);
      }
      throw new ApiProblemError({
        type: "about:blank",
        title: `Request failed (${res.status})`,
        status: res.status,
        code: "internal",
      });
    }
    return json as T;
  }

  /** A currently valid scoped token — the WebSocket handshake needs the
   * raw token because it cannot send an Authorization header. */
  async ensureToken(): Promise<string> {
    return this.freshToken();
  }
}

/** Mint a browser asset token for <img>/<video> query params (type 5). */
export async function mintAssetToken(conn: ServerConnection): Promise<{ token: string; expiresAt: number }> {
  const res = await conn.request<{ token: string; expiresAt: string }>("/v1/auth/asset-token", {
    method: "POST",
  });
  return { token: res.token, expiresAt: Date.parse(res.expiresAt) };
}
